import { Link, useNavigate } from "react-router-dom";
import {
    Shield,
    LogIn,
    UserPlus,
    LogOut,
    Mail,
    FileText,
    MessageSquare,
    Sheet,
    Presentation,
    FolderOpen,
    Settings
} from "lucide-react";
import { Button } from "@/components/ui/button";
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle
} from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { useLogout, useSession } from "@/api/hooks";

interface AppTile {
    name: string;
    description: string;
    icon: typeof Mail;
    href: string | null;
}

const appTiles: AppTile[] = [
    {
        name: "Mail",
        description: "Email, calendar, contacts",
        icon: Mail,
        href: import.meta.env.VITE_MAIL_WEB_URL ?? null
    },
    {
        name: "Connect",
        description: "Chat, channels, meetings",
        icon: MessageSquare,
        href: import.meta.env.VITE_CONNECT_WEB_URL ?? null
    },
    {
        name: "Drive",
        description: "Cloud file storage",
        icon: FolderOpen,
        href: import.meta.env.VITE_DRIVE_WEB_URL ?? null
    },
    {
        name: "Docs",
        description: "Word-compatible",
        icon: FileText,
        href: import.meta.env.VITE_DOCS_WEB_URL ?? null
    },
    {
        name: "Sheets",
        description: "Excel-compatible",
        icon: Sheet,
        href: import.meta.env.VITE_SHEET_WEB_URL ?? null
    },
    {
        name: "Presentation",
        description: "PowerPoint-compatible",
        icon: Presentation,
        href: import.meta.env.VITE_PRESENT_WEB_URL ?? null
    },
    {
        name: "Admin",
        description: "Tenant administration",
        icon: Settings,
        href: import.meta.env.VITE_ADMIN_WEB_URL ?? null
    }
];

export function HomePage() {
    const session = useSession();
    const logout = useLogout();
    const navigate = useNavigate();

    if (session.isPending) {
        return (
            <Card className="w-full">
                <CardHeader>
                    <CardTitle>Athavita Identity</CardTitle>
                    <CardDescription>Checking your session…</CardDescription>
                </CardHeader>
            </Card>
        );
    }

    if (session.data?.isAuthenticated) {
        return (
            <Card className="w-full max-w-xl">
                <CardHeader>
                    <CardTitle>Welcome, {session.data.displayName ?? session.data.email}</CardTitle>
                    <CardDescription>
                        {session.data.email}
                        {session.data.roles.length > 0 ? (
                            <> · {session.data.roles.join(", ")}</>
                        ) : null}
                    </CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                    <div>
                        <div className="mb-2 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                            Open an app
                        </div>
                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                            {appTiles.map((app) => {
                                const Icon = app.icon;
                                const available = Boolean(app.href);
                                const content = (
                                    <>
                                        <Icon className={cn("mb-2 h-6 w-6", available ? "text-primary" : "text-muted-foreground")} aria-hidden />
                                        <div className="text-sm font-semibold">{app.name}</div>
                                        <div className="text-xs text-muted-foreground">{app.description}</div>
                                        {!available ? (
                                            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                                                Coming soon
                                            </div>
                                        ) : null}
                                    </>
                                );
                                return available ? (
                                    <a
                                        key={app.name}
                                        href={app.href!}
                                        className="flex flex-col items-start rounded-lg border bg-card p-3 transition-colors hover:border-primary/50 hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                                    >
                                        {content}
                                    </a>
                                ) : (
                                    <div
                                        key={app.name}
                                        className="flex flex-col items-start rounded-lg border border-dashed bg-muted/30 p-3 opacity-70"
                                        aria-disabled="true"
                                    >
                                        {content}
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                    <div className="flex flex-col gap-2 sm:flex-row">
                        <Button asChild variant="outline" className="flex-1">
                            <Link to="/profile">Your profile</Link>
                        </Button>
                        <Button asChild variant="outline" className="flex-1">
                            <Link to="/change-password">Change password</Link>
                        </Button>
                        <Button
                            variant="outline"
                            className="flex-1"
                            disabled={logout.isPending}
                            onClick={() =>
                                logout.mutate(undefined, {
                                    onSettled: () => navigate("/", { replace: true })
                                })
                            }
                        >
                            <LogOut className="h-4 w-4" />
                            {logout.isPending ? "Signing out…" : "Sign out"}
                        </Button>
                    </div>
                </CardContent>
                <CardFooter className="text-xs text-muted-foreground">
                    Tiles light up automatically as their VITE_*_WEB_URL is configured for the deployment.
                </CardFooter>
            </Card>
        );
    }

    return (
        <Card className="w-full">
            <CardHeader className="text-center">
                <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                    <Shield className="h-6 w-6 text-primary" aria-hidden />
                </div>
                <CardTitle>Athavita Identity</CardTitle>
                <CardDescription>
                    Single sign-on for the Athavita Enterprise Suite.
                </CardDescription>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
                <Button asChild size="lg">
                    <Link to="/login">
                        <LogIn className="h-4 w-4" /> Sign in
                    </Link>
                </Button>
                <Button asChild size="lg" variant="outline">
                    <Link to="/signup">
                        <UserPlus className="h-4 w-4" /> Create a tenant
                    </Link>
                </Button>
            </CardContent>
        </Card>
    );
}
